// Pagina IMPOSTAZIONI / ACCOUNT
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { PageContainer } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Select, Field } from '@/components/ui/Input';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge, Divider, Spinner } from '@/components/ui/Misc';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import { useNotesStore } from '@/store/notesStore';
import { usePersonalTasksStore } from '@/store/personalTasksStore';
import { useProfitTasksStore } from '@/store/profitTasksStore';
import { useRemindersStore } from '@/store/remindersStore';
import { useCookieConsentStore } from '@/store/cookieConsentStore';
import { toast } from '@/hooks/useToast';
import { confirmDialog } from '@/components/ui/Confirm';
import { CURRENCIES } from '@/utils/constants';
import { formatDateTime, cn } from '@/utils';
import { requestPermission, checkPermission } from '@/lib/notifications';
import { showRewardedAd, isAdMobConfigured } from '@/lib/admob';
import { useI18n, LANGUAGES } from '@/i18n';
import type { Lang } from '@/i18n';
import { ReportProblemModal } from '@/components/ReportProblem';
import {
  Settings as SettingsIcon, Moon, Sun, Monitor, Cloud, Database, CheckCircle2,
  AlertCircle, Shield, LogOut, User as UserIcon, RefreshCw, Bell, CloudOff,
  Heart, Megaphone, Bug, Languages, Cookie,
  Globe,
  FileText,
  Copyright,
  Scale,
  ExternalLink,
} from 'lucide-react';

export function SettingsPage() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const { settings, loaded, save } = useSettingsStore();
  const { user, logout, syncNow } = useAuthStore();
  const notesStore = useNotesStore();
  const personalStore = usePersonalTasksStore();
  const profitStore = useProfitTasksStore();
  const remindersStore = useRemindersStore();
  const cookieStore = useCookieConsentStore();

  const [syncing, setSyncing] = useState(false);
  const [notifPerm, setNotifPerm] = useState<boolean | null>(null);
  const [adLoading, setAdLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    checkPermission().then(setNotifPerm);
  }, []);

  if (!loaded) {
    return (
      <>
        <Header title={t('settings.title')} icon={<SettingsIcon size={18} />} />
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      </>
    );
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await syncNow();
      if (r.ok) {
        toast.success(r.message);
        await save({ last_sync_at: new Date().toISOString() });
      } else {
        toast.error(r.message);
      }
    } catch (e: any) {
      toast.error(`${t('sync.failed')}: ${e?.message ?? ''}`);
    } finally {
      setSyncing(false);
    }
  }

  async function handleLogout() {
    confirmDialog({
      title: t('settings.logout'),
      message: t('settings.not_logged_in'),
      confirmText: t('settings.logout'),
      danger: true,
      onConfirm: async () => {
        await logout();
        toast.success(t('settings.logout'));
        navigate('/notes');
      },
    });
  }

  async function handleNotifPermission() {
    const ok = await requestPermission();
    setNotifPerm(ok);
    if (ok) {
      toast.success(t('settings.notif_enable'));
      await save({ notifications_enabled: true });
    } else {
      toast.error(t('settings.notif_enable'));
    }
  }

  async function handleWatchAd() {
    if (!isAdMobConfigured()) {
      toast.error(t('settings.ads_not_configured'));
      return;
    }
    setAdLoading(true);
    try {
      const r = await showRewardedAd();
      if (r.success && r.reward) {
        toast.success(t('settings.ads_thanks'));
      } else if (r.message === 'ads_not_configured') {
        toast.error(t('settings.ads_not_configured'));
      } else {
        toast.error(t('settings.ads_error'));
      }
    } catch {
      toast.error(t('settings.ads_error'));
    } finally {
      setAdLoading(false);
    }
  }

  const totalLocal = notesStore.notes.length + personalStore.tasks.length + profitStore.tasks.length + remindersStore.reminders.length;

  return (
    <>
      <Header
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        icon={<div className="w-9 h-9 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center"><SettingsIcon size={18} className="text-slate-600 dark:text-slate-300" /></div>}
      />

      <PageContainer className="space-y-5">
        {/* ACCOUNT */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <UserIcon size={15} /> {t('settings.section_account')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-3">
            {user ? (
              <>
                <div className="flex items-center gap-3">
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 text-white flex items-center justify-center font-bold">
                      {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">{user.displayName || 'Utente'}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                    <Badge className="mt-1 text-[10px]">
                      {user.provider === 'google' ? 'Google' : user.provider === 'email' ? 'Email' : user.provider}
                    </Badge>
                  </div>
                </div>
                <Divider />
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">{t('settings.last_sync')}</span>
                    <span className="font-medium text-slate-600 dark:text-slate-300">{formatDateTime(settings.last_sync_at)}</span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full" onClick={handleSync} loading={syncing}>
                    <RefreshCw size={14} /> {t('settings.sync_now')}
                  </Button>
                  <Button variant="ghost" size="sm" className="w-full text-rose-600" onClick={handleLogout}>
                    <LogOut size={14} /> {t('settings.logout')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
                  <CloudOff size={14} className="mt-0.5 shrink-0" />
                  <p>{t('settings.not_logged_in')}</p>
                </div>
                <Button className="w-full" onClick={() => navigate('/auth')}>
                  <Cloud size={16} /> {t('settings.login_register')}
                </Button>
              </>
            )}
          </CardBody>
        </Card>

        {/* ASPETTO */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Sun size={15} /> {t('settings.section_appearance')}
            </h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-3 gap-2">
              {([
                { v: 'light', label: t('settings.theme_light'), icon: Sun },
                { v: 'dark', label: t('settings.theme_dark'), icon: Moon },
                { v: 'system', label: t('settings.theme_system'), icon: Monitor },
              ] as const).map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  onClick={() => save({ theme: v })}
                  className={cn(
                    'flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all',
                    settings.theme === v
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                      : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400',
                  )}
                >
                  <Icon size={18} />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* LINGUA */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Languages size={15} /> {t('settings.section_language')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-2">
            <button
              onClick={() => setLang(detectSystemLangForButton())}
              className={cn(
                'w-full flex items-center justify-between p-2.5 rounded-lg transition-colors',
                lang === detectSystemLangForButton()
                  ? 'bg-brand-50 dark:bg-brand-900/30'
                  : 'bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800',
              )}
            >
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">🌐 {t('settings.language_system')}</span>
              {lang === detectSystemLangForButton() && <CheckCircle2 size={16} className="text-brand-600" />}
            </button>
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code as Lang)}
                className={cn(
                  'w-full flex items-center justify-between p-2.5 rounded-lg transition-colors',
                  lang === l.code
                    ? 'bg-brand-50 dark:bg-brand-900/30'
                    : 'bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
              >
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{l.flag} {l.label}</span>
                {lang === l.code && <CheckCircle2 size={16} className="text-brand-600" />}
              </button>
            ))}
          </CardBody>
        </Card>

        {/* DATI LOCALI */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Database size={15} /> {t('settings.section_data')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                <p className="text-base font-bold text-amber-500">{notesStore.notes.length}</p>
                <p className="text-[10px] text-slate-400">{t('nav.notes')}</p>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                <p className="text-base font-bold text-blue-500">{personalStore.tasks.length}</p>
                <p className="text-[10px] text-slate-400">{t('nav.personal')}</p>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                <p className="text-base font-bold text-emerald-500">{profitStore.tasks.length}</p>
                <p className="text-[10px] text-slate-400">{t('nav.profit')}</p>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                <p className="text-base font-bold text-rose-500">{remindersStore.reminders.length}</p>
                <p className="text-[10px] text-slate-400">{t('nav.reminders')}</p>
              </div>
            </div>
            <Field label={t('settings.currency')}>
              <Select value={settings.default_currency} onChange={(e) => save({ default_currency: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </Select>
            </Field>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Shield size={12} />
              <span>{t('settings.offline_first')} ({totalLocal})</span>
            </div>
          </CardBody>
        </Card>

        {/* NOTIFICHE */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Bell size={15} /> {t('settings.section_notifications')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className={cn(
              'rounded-lg p-3 flex items-start gap-2 text-xs',
              notifPerm
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
            )}>
              {notifPerm ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertCircle size={14} className="mt-0.5 shrink-0" />}
              <div>
                <p>{notifPerm ? t('settings.notif_active') : t('settings.notif_inactive')}</p>
                {notifPerm && <p className="mt-1.5 text-[11px] opacity-80">{t('settings.notif_active_hint')}</p>}
              </div>
            </div>
            {!notifPerm && (
              <Button variant="outline" size="sm" className="w-full" onClick={handleNotifPermission}>
                <Bell size={14} /> {t('settings.notif_enable')}
              </Button>
            )}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('settings.notif_toggle')}</span>
              <button
                onClick={() => save({ notifications_enabled: !settings.notifications_enabled })}
                className={cn('w-10 h-5 rounded-full relative transition-colors', settings.notifications_enabled ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600')}
              >
                <div className={cn('absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform', settings.notifications_enabled && 'translate-x-5')} />
              </button>
            </div>
          </CardBody>
        </Card>

        {/* SUPPORTA IL PROGETTO (AdMob) */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Heart size={15} className="text-rose-500" /> {t('settings.section_ads')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{t('settings.ads_desc')}</p>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleWatchAd}
              loading={adLoading}
              disabled={!isAdMobConfigured()}
            >
              <Megaphone size={16} /> {adLoading ? t('settings.ads_loading') : t('settings.ads_watch')}
            </Button>
            {!isAdMobConfigured() && (
              <p className="text-[11px] text-amber-500 text-center">{t('settings.ads_not_configured')}</p>
            )}
          </CardBody>
        </Card>

        {/* SEGNALA UN PROBLEMA */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Bug size={15} /> {t('settings.section_report')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{t('settings.report_desc')}</p>
            <Button variant="outline" className="w-full" onClick={() => setReportOpen(true)}>
              <Bug size={16} /> {t('settings.report_button')}
            </Button>
          </CardBody>
        </Card>

        {/* COOKIE PREFERENCES (riapri banner) */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Cookie size={15} /> Cookie
            </h3>
          </CardHeader>
          <CardBody>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {cookieStore.hasDecided ? '✓ ' + t('cookie.save_preferences') : t('cookie.message')}
            </p>
            <Button variant="ghost" size="sm" onClick={() => cookieStore.reset()}>
              {t('cookie.customize')}
            </Button>
          </CardBody>
        </Card>

        
         <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Scale size={15} /> {t('settings.section_legal')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => navigate('/privacy')}
            >
              <FileText size={14} className="mr-2 text-slate-400" />
              {t('privacy.title')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => navigate('/terms')}
            >
              <FileText size={14} className="mr-2 text-slate-400" />
              {t('terms.title')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={() => navigate('/copyright')}
            >
              <Copyright size={14} className="mr-2 text-slate-400" />
              {t('copyright.title')}
            </Button>
          </CardBody>
        </Card>

        {/* WEB ACCESS */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <Globe size={15} /> {t('settings.section_web')}
            </h3>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              {t('settings.web_desc')}
            </p>
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                piranotes.com
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://piranote.com', '_blank')}
              >
                <ExternalLink size={14} className="mr-1.5" />
                {t('settings.web_open')}
              </Button>
            </div>
          </CardBody>
        </Card>
        {/* INFO */}
        <Card>
          <CardBody>
            <div className="text-center space-y-1.5">
              <p className="text-base font-bold bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent">
                {t('settings.about_app')}
              </p>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {t('settings.about_version', { version: '1.0' })}
              </p>
              <p className="text-xs text-slate-400 max-w-xs mx-auto mt-2 leading-relaxed">
                {t('settings.about_desc')}
              </p>
              <p className="text-[11px] text-slate-400 mt-2">{t('settings.about_offline')}</p>
              <p className="text-[11px] text-slate-400">{t('settings.about_data')}</p>
            </div>
          </CardBody>
        </Card>
      </PageContainer>

      <ReportProblemModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onRequireLogin={() => {
          setReportOpen(false);
          navigate('/auth');
        }}
      />
    </>
  );
}

// Helper per mostrare la lingua di sistema nel picker
function detectSystemLangForButton(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  const nav = (navigator.language || 'en').toLowerCase();
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('tr')) return 'tr';
  return 'en';
}
